import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import type { DirectoryEntry } from '@/lib/seo/directory';
import api from '@/services/api';
import {
  DiscoveryCategory,
  DiscoveryFilters,
  DiscoverySurface,
  ProviderCard,
  Region,
  SearchProvidersResponse,
} from '@/types/discovery';

export const discoveryKeys = {
  all: ['discovery'] as const,
  categories: () => [...discoveryKeys.all, 'categories'] as const,
  highlights: (region: Region) =>
    [...discoveryKeys.all, 'highlights', region.uf ?? 'BR', region.city ?? ''] as const,
  search: (region: Region, filters: DiscoveryFilters) =>
    [...discoveryKeys.all, 'search', region.uf ?? 'BR', region.city ?? '', filters] as const,
};

const PAGE_SIZE = 20;

function toParams(region: Region, filters: DiscoveryFilters) {
  return {
    uf: region.uf ?? undefined,
    city: region.city ?? undefined,
    q: filters.q?.trim() || undefined,
    categories: filters.categories?.length ? filters.categories.join(',') : undefined,
    minPrice: filters.minPrice,
    maxPrice: filters.maxPrice,
    bookableOnly: filters.bookableOnly ? 'true' : undefined,
    hasGallery: filters.hasGallery ? 'true' : undefined,
    sort: filters.sort,
  };
}

export function useDiscoveryCategories() {
  return useQuery({
    queryKey: discoveryKeys.categories(),
    queryFn: async () => {
      const res = await api.get<DiscoveryCategory[]>('/public/discovery/categories');
      return res.data;
    },
    staleTime: 60 * 60 * 1000,
  });
}

/** Páginas do diretório com prestadores suficientes (`/profissionais/...`). */
export function useDirectoryEntries() {
  return useQuery({
    queryKey: [...discoveryKeys.all, 'directory'] as const,
    queryFn: async () => {
      const res = await api.get<DirectoryEntry[]>('/public/discovery/directory');
      return res.data;
    },
    staleTime: 60 * 60 * 1000,
  });
}

/** Carrossel da home: principais da região (+ 1 patrocinado no topo). */
export function useDiscoveryHighlights(region: Region, limit = 10) {
  return useQuery({
    queryKey: discoveryKeys.highlights(region),
    queryFn: async () => {
      const res = await api.get<ProviderCard[]>('/public/discovery/highlights', {
        params: { uf: region.uf ?? undefined, city: region.city ?? undefined, limit },
      });
      return res.data;
    },
    staleTime: 5 * 60 * 1000,
  });
}

/** Busca paginada (scroll infinito). */
export function useSearchProviders(region: Region, filters: DiscoveryFilters) {
  return useInfiniteQuery({
    queryKey: discoveryKeys.search(region, filters),
    initialPageParam: 1,
    queryFn: async ({ pageParam }) => {
      const res = await api.get<SearchProvidersResponse>('/public/discovery/providers', {
        params: { ...toParams(region, filters), page: pageParam, limit: PAGE_SIZE },
      });
      return res.data;
    },
    getNextPageParam: (lastPage) => (lastPage.hasMore ? lastPage.page + 1 : undefined),
    staleTime: 60 * 1000,
  });
}

/**
 * Impressão/clique. Fire-and-forget: métrica nunca pode quebrar a navegação
 * nem atrasar o toque do usuário.
 */
export function trackDiscoveryEvent(input: {
  providerId: string;
  type: 'IMPRESSION' | 'CLICK';
  surface: DiscoverySurface;
  sponsorshipId?: string | null;
  query?: string;
  region?: Region;
  position?: number;
}): void {
  void api
    .post('/public/discovery/events', {
      providerId: input.providerId,
      type: input.type,
      surface: input.surface,
      sponsorshipId: input.sponsorshipId ?? undefined,
      query: input.query?.trim() || undefined,
      uf: input.region?.uf ?? undefined,
      city: input.region?.city ?? undefined,
      position: input.position,
    })
    .catch(() => undefined);
}
